import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Card, Container, Form } from 'react-bootstrap';
import SingleHistoryElement from './SingleHistoryElement';
import SkeletonLoader from '../../../elements/SkeletonLoader';
import { getConfigurations } from '../../../../modules/config';
import { useParams } from 'react-router-dom';
import { fetchTestCycleHistory } from '../../../../modules/testcycles';
import { getCurrentProject } from '../../../../modules/project';
import { getProjectReleaseVersions } from '../../../../modules/jira';
import { getJenkinsJobs } from '../../../../modules/automation';

const TestCycleHistory = ({ entityId, users }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [environments, setEnvironments] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [sort, setSort] = useState('Newest First');
  const [releases, setReleases] = useState([]);
  const [jobs, setJobs] = useState([]);
  const { projectId } = useParams();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await fetchTestCycleHistory(entityId);
        setData(response.data);
        setError(null);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    const fetchOtherData = async () => {
      try {
        const [statusesResponse, environmentsResponse, jobsResponse] =
          await Promise.all([
            getConfigurations('testcyclestatus', projectId),
            getConfigurations('environments', projectId),
            getJenkinsJobs(projectId),
          ]);
        setEnvironments(environmentsResponse?.data || []);
        setStatuses(statusesResponse?.data || []);
        setJobs(jobsResponse?.data || []);

        const response = await getCurrentProject(projectId);
        if (response?.linked_project_id) {
          const versionResponse = await getProjectReleaseVersions(
            response.linked_project_id,
            projectId
          );
          if (versionResponse) {
            setReleases(versionResponse?.values);
          }
        }

        setError(null);
      } catch (error) {
        setError(error.message);
      }
    };

    fetchData();
    fetchOtherData();

    return () => {
      setData([]);
    };
  }, [entityId, projectId]);

  const sortedData = useMemo(() => {
    return [...data].sort((a, b) =>
      sort === 'Newest First'
        ? new Date(b.created_at) - new Date(a.created_at)
        : new Date(a.created_at) - new Date(b.created_at)
    );
  }, [data, sort]);

  const groupByDateTime = useCallback(() => {
    return sortedData.reduce((acc, item) => {
      const dateTimeKey = `${new Date(item.created_at).toISOString().slice(0, 16)}-${item.event_type}`;

      if (!acc[dateTimeKey]) {
        acc[dateTimeKey] = [];
      }

      acc[dateTimeKey].push(item);
      return acc;
    }, {});
  }, [sortedData]);

  const groupedData = Object.values(groupByDateTime());

  return (
    <Card className="shadow-sm">
      <Card.Header className="d-flex justify-content-between align-items-center" style={{ height: '50px' }}>
        <h5 className="mb-0">History</h5>
        {groupedData.length > 0 && (
          <Form.Select style={{ width: '150px' }} value={sort} onChange={(e) => setSort(e.target.value)}>
            <option>Newest First</option>
            <option>Oldest First</option>
          </Form.Select>
        )}
      </Card.Header>
      <Card.Body>
        <Container fluid>
          {loading && <SkeletonLoader />}
          {error && <p>{error}</p>}
          {groupedData.map((item, index) => (
            <SingleHistoryElement
              key={index}
              item={item}
              users={users}
              jobs={jobs}
              environments={environments}
              statuses={statuses}
              releases={releases}
            />
          ))}
        </Container>
      </Card.Body>
    </Card>
  );
};

export default TestCycleHistory;
